/* AIDeclaration — bloco de declaração de uso de IA conforme
 * Manual Sementes de Futuro em Defesa 2026.
 * Se ai_used = true e ai_human_verified = false, envio é bloqueado.
 */
import { Sparkles, ShieldCheck, AlertTriangle } from "lucide-react";
import { AI_TOOLS, AI_PURPOSES } from "@/lib/constants";

export default function AIDeclaration({ form, onChange }) {
  const set = (key, value) => onChange({ ...form, [key]: value });
  const aiUsed = !!form.ai_used;
  const verified = !!form.ai_human_verified;

  return (
    <div className="space-y-4" data-testid="ai-declaration">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
        <ShieldCheck size={18} className="text-amber-700 shrink-0 mt-0.5" />
        <p className="text-[12.5px] text-amber-900 dark:text-amber-100 leading-snug">
          <strong>A autoria é humana.</strong> A IA pode apoiar organização, revisão e análise,
          mas o pesquisador responde pelo conteúdo final da Semente.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="ai-used-toggle">
        <input
          type="checkbox"
          checked={aiUsed}
          onChange={(e) => set("ai_used", e.target.checked)}
          className="mt-1 accent-[var(--gold)]"
        />
        <span className="flex-1">
          <span className="font-semibold text-sm flex items-center gap-1.5">
            <Sparkles size={13} /> Utilizei IA no preenchimento desta Semente
          </span>
          <span className="block text-[11.5px] text-[var(--text-muted)] mt-0.5">
            Marque se qualquer campo foi total ou parcialmente sugerido por IA (Gemini, ChatGPT, etc.).
          </span>
        </span>
      </label>

      {aiUsed && (
        <div className="pl-7 space-y-3 border-l-2 border-amber-300 dark:border-amber-800/60">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label-mil block mb-1">Ferramenta utilizada</label>
              <select
                className="input-mil"
                value={form.ai_tool_name || ""}
                onChange={(e) => set("ai_tool_name", e.target.value)}
                data-testid="ai-tool-select"
              >
                <option value="">— selecione —</option>
                {AI_TOOLS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mil block mb-1">Finalidade do uso</label>
              <select
                className="input-mil"
                value={form.ai_usage_purpose || ""}
                onChange={(e) => set("ai_usage_purpose", e.target.value)}
                data-testid="ai-purpose-select"
              >
                <option value="">— selecione —</option>
                {AI_PURPOSES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40" data-testid="ai-verified-toggle">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => set("ai_human_verified", e.target.checked)}
              className="mt-1 accent-emerald-600"
            />
            <span className="flex-1">
              <span className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
                Confirmo que revisei manualmente todos os campos preenchidos com apoio de IA
              </span>
              <span className="block text-[11.5px] text-emerald-800 dark:text-emerald-200 mt-0.5">
                Obrigatório para envio à Revisão Humana quando houver uso de IA.
              </span>
            </span>
          </label>

          {!verified && (
            <div className="flex items-start gap-2 text-[12px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/40 rounded p-2.5" data-testid="ai-verified-warning">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>
                Enquanto a revisão manual não for confirmada, o envio para Revisão Humana permanece bloqueado.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Regra de bloqueio para uso no submit ───────────────────────────────
export function isAiDeclarationValid(form) {
  if (!form?.ai_used) return true;
  return !!form?.ai_human_verified;
}
